import { MapPin } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import type { DeliveryTabGridEntry } from "@/lib/api/dispatchTypes";
import { deliveryMapUrl, deliveryPinIsApproximate } from "@/modules/pos/lib/deliveryMap";
import { addressForDisplay } from "@/modules/pos/lib/deliveryWaiting";

/**
 * The «Deliver to» address line of a delivery bag on the Delivery tab (B6 ·
 * P24 G24-M1, P28a C10 carry). The typed address never prints a pasted Maps
 * link or bare `lat,lng` (a pin mark says one was typed — the driver's app
 * opens it). A bill accepted from a PINNED storefront request carries the
 * customer's pin (`deliveryPin`): the mark then opens it on the map through
 * #238's `deliveryMapUrl` (built from the numbers only), and an area-centre
 * pin says it is approximate, as on the request card.
 */
export function DeliveryAddressLine({
  bill,
  testId,
}: {
  bill: DeliveryTabGridEntry;
  /** e.g. `waiting-address-12`; the pin / map / approximate ids derive from it. */
  testId?: string;
}) {
  const t = useTranslate();
  const address = addressForDisplay(bill.deliveryAddress);
  const mapUrl = bill.channel === "DELIVERY" ? deliveryMapUrl(bill.deliveryPin) : null;
  const approximate = mapUrl !== null && deliveryPinIsApproximate(bill.deliveryPin);
  const hasPin = mapUrl !== null || address.hasPin;
  const pinTestId = testId?.replace("-address-", "-pin-");
  return (
    <span data-ui="waiting-address" className="flex min-w-0 items-center" data-testid={testId}>
      {mapUrl ? (
        <a
          href={mapUrl}
          target="_blank"
          rel="noreferrer noopener"
          data-ui="waiting-address-map"
          aria-label={t((m) => m.pos.onlineOrderMap)}
          title={t((m) => m.pos.onlineOrderMap)}
          data-testid={pinTestId ? `${pinTestId}-map` : undefined}
          className="flex shrink-0 items-center"
          // A link inside a selectable row: opening the map never toggles it.
          onClick={(event) => event.stopPropagation()}
        >
          <MapPin aria-hidden="true" size={12} strokeWidth={2} data-ui="waiting-address-pin" />
        </a>
      ) : address.hasPin ? (
        <MapPin
          aria-label={t((m) => m.dispatch.waitingMapPin)}
          role="img"
          size={12}
          strokeWidth={2}
          data-ui="waiting-address-pin"
          data-testid={pinTestId}
          className="shrink-0"
        />
      ) : null}
      <span className="truncate">
        <bdi>
          {address.text ||
            (hasPin ? t((m) => m.dispatch.waitingMapPin) : t((m) => m.dispatch.waitingNoAddress))}
        </bdi>
      </span>
      {approximate ? (
        <span
          data-ui="waiting-address-approximate"
          className="shrink-0 text-[color:var(--warning)]"
          data-testid={pinTestId ? `${pinTestId}-approximate` : undefined}
        >
          {t((m) => m.pos.onlineOrderMapApproximate)}
        </span>
      ) : null}
    </span>
  );
}
